import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getDesign,
  updateDesign,
  deleteDesign,
  uploadKnowledgeBaseFile,
  deleteWorkflowFile,
  renameWorkflowFile,
  duplicateDesign,
  resolveFileUrl,
} from '../services/api';
import NewProjectModal from './NewProjectModal';
import { confirm as confirmDialog, alertDialog } from './ConfirmDialog';
import { formatRelative, formatRelativeCompact } from '../utils/time';
import { getStarredProjects, toggleStarred } from '../services/starred';
// exportFromBlocks drags the whole canvas render stack (BlockNote, mermaid,
// vega, the DOCX exporter — several MB) into whatever imports it. ProjectTree
// mounts on the HOME page, so a static import here defeats route-splitting.
// Loaded on demand inside the export click handler instead.
const loadExportBlocks = () => import('../services/exportFromBlocks').then((m) => m.exportBlocks);
import ProjectTreeSkeleton from './ProjectTreeSkeleton';

/**
 * ProjectTree — the leftrail tree shown by both NewChat and DesignView.
 *
 * Replaces the duplicated inline blocks that previously lived in those
 * pages (NewChat.js:395-486, DesignView.js:1028-1113). Renders four
 * tree levels:
 *
 *   l1  Project (derived from BRD filename or journeyOverview.name)
 *   l2  Design (with v{N} pill)
 *   l3  Inputs / Outputs / Chat group headers
 *   l4  Individual files / version rows under each group
 *
 * The Inputs / Outputs / Chat groups are collapsed by default and expand
 * on chevron click. The Outputs group has a nested expandable "Design
 * Document" entry whose children are the per-version history (v3/v2/v1
 * with relative timestamps).
 *
 * Per-design lazy data (full document set, chat count) is fetched only
 * when the design row is expanded — avoids N+1 fan-out on tree mount.
 */

// ─── Avatar palette (deterministic per project name)

const AVATAR_PALETTE = [
  { bg: '#FBE4E4', fg: '#B42318' },
  { bg: '#E1ECFD', fg: '#1D4ED8' },
  { bg: '#FEF3C7', fg: '#92400E' },
  { bg: '#E9D5FF', fg: '#6B21A8' },
  { bg: '#D1FAE5', fg: '#065F46' },
  { bg: '#FCE7F3', fg: '#9D174D' },
  { bg: '#CFFAFE', fg: '#155E75' },
  { bg: '#FEE2E2', fg: '#9A3412' },
];
function colorSlotFor(name) {
  let h = 0;
  for (const ch of (name || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

// ─── Inline SVG icons

function IconPlus() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <line x1="7" y1="2" x2="7" y2="12" /><line x1="2" y1="7" x2="12" y2="7" />
    </svg>
  );
}
function IconPencil() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 2.5l2 2-7 7H2.5v-2l7-7z" />
    </svg>
  );
}
function IconTrash() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="2,4 12,4" /><path d="M5 4V2h4v2" /><path d="M3 4l1 8h6l1-8" />
    </svg>
  );
}
function IconArrowEnter() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4,5 2,7 4,9" /><path d="M2 7h7V3" />
    </svg>
  );
}
function IconCopy() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="7" height="7" rx="1" /><path d="M2 8V2a1 1 0 011-1h6" />
    </svg>
  );
}

function FolderIcon({ expanded }) {
  const fill = expanded ? '#C09030' : '#8A8A94';
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill={fill}>
      <path d="M1.5 3.5v9c0 .83.67 1.5 1.5 1.5h10c.83 0 1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5H7L5.5 2.5H3c-.83 0-1.5.67-1.5 1z" opacity={expanded ? "1" : "0.75"} />
    </svg>
  );
}

// ─── Project / title derivation (lifted from NewChat.js + DesignView.js)

// Lower-case helper: returns true if the BRD filename starts with the
// project-doc boilerplate the consultants always use ("ajo en …",
// "BRD …", "<campaign> Business Requirements Document"). Used to drop
// that prefix from the design title so the row reads as the actual
// campaign name, not the document template.
const BOILERPLATE_PREFIXES = [
  'ajo en ', 'ajo_en_', 'ajo-en-',
  'brd ', 'brd_', 'brd-',
  'business requirements document', 'business_requirements_document',
];

function stripBoilerplate(s) {
  let out = (s || '').trim();
  if (!out) return '';
  // If the filename uses " - " or " — " as a separator, the meaningful
  // part is usually the second half: "<template name> - <campaign>".
  // Take the last segment after any dash separator with surrounding spaces.
  const dashSplit = out.split(/\s+[-–—]\s+/);
  if (dashSplit.length > 1 && dashSplit[dashSplit.length - 1].trim()) {
    out = dashSplit[dashSplit.length - 1].trim();
  } else {
    const lower = out.toLowerCase();
    for (const pref of BOILERPLATE_PREFIXES) {
      if (lower.startsWith(pref)) {
        out = out.slice(pref.length).trim();
        break;
      }
    }
  }
  // Title case the cleaned remainder.
  return out
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

export function getProjectName(design) {
  // designName is the user's typed label from the New Project modal.
  // Use it verbatim as the project group key — without this, a user who
  // names a project "test" sees their design grouped under "Birthday"
  // (the first word of the BRD filename), which is the bug from the
  // 2026-05-03 screenshot. designName has authority over filename.
  const dn = (design?.designName || '').trim();
  if (dn && dn !== 'TBD' && !looksLikeBrdFilename(dn)) return dn;
  // Prefer the journey overview name once the agent has populated it.
  const jname = design?.journeyOverview?.name;
  if (jname && jname !== 'TBD') {
    const first = jname.split(/\s+/)[0];
    if (first && first.length > 1) return first;
  }
  // Fallback to the cleaned filename's first word.
  const docKey = design?.brdDocumentS3Key || design?.documentS3Key || '';
  if (docKey) {
    const filename = docKey.split('/').pop() || '';
    const cleaned = stripBoilerplate(filename.split('.')[0] || '');
    if (cleaned) {
      const first = cleaned.split(' ')[0];
      if (first && first.length > 1) return first;
    }
  }
  return 'AJO';
}

export function getDesignTitle(design) {
  // designName is set when the user typed a name in the New Project
  // modal — that's the canonical project label and beats every fallback.
  const dn = design?.designName;
  if (dn && dn !== 'TBD' && !looksLikeBrdFilename(dn)) return dn;
  if (design?.journeyOverview?.name && design.journeyOverview.name !== 'TBD') {
    return design.journeyOverview.name;
  }
  const docKey = design?.brdDocumentS3Key || design?.documentS3Key || '';
  if (docKey) {
    const filename = docKey.split('/').pop() || '';
    const cleaned = stripBoilerplate(filename.split('.')[0] || '');
    if (cleaned) return cleaned;
  }
  return 'Design Document';
}

// Per-surface title helpers. Each of these returns the appropriate label
// for one of the THREE independent rename surfaces (workflow folder,
// document version row, canvas H1). The rule: each surface has its own
// DDB attribute; if that attribute is unset the surface falls back to
// `designName` (the modal-typed name) so a freshly-created design shows
// the same name everywhere by default. Renaming any one surface ONLY
// writes its own attribute, so the other two surfaces continue to read
// `designName` and stay unchanged. This is what makes the three names
// independent after first rename.
export function getWorkflowTitle(design) {
  const wn = (design?.workflowName || '').trim();
  if (wn) return wn;
  return getDesignTitle(design);
}

export function getDocumentTitle(design) {
  const dn = (design?.documentName || '').trim();
  if (dn) return dn;
  return getDesignTitle(design);
}

export function getCanvasTitle(design) {
  const t = (design?.title || '').trim();
  if (t) return t;
  return getDesignTitle(design);
}

// Detect when `designName` was auto-derived from the BRD filename by an
// older orchestrator path (before the modal landed). In that case fall
// through to the cleaner stripBoilerplate logic so existing rows don't
// stay stuck on the long template name.
function looksLikeBrdFilename(s) {
  const lower = String(s || '').toLowerCase();
  return /(business[_ ]requirements|brd[_ ]document|^ajo[ _]en|\.docx?$|\.pdf$)/.test(lower);
}

// Pull a short campaign-type label off the design — surfaces stuff like
// "Email Campaign", "Birthday Coupon" so the design row is more than a
// long auto-generated filename. Falls back to first useful piece of the
// extracted BRD summary if no journey-overview field exists yet.
export function getDesignSubtitle(design) {
  const jo = design?.journeyOverview || design?.ajoDesignDocument?.journeyOverview || {};
  const candidates = [
    jo.useCase,
    jo.campaignType,
    jo.primaryChannel,
    design?.designDescription,
  ];
  for (const v of candidates) {
    if (typeof v === 'string' && v.trim() && v.trim() !== 'TBD') {
      // Trim long values so the row stays single-line in the rail.
      const s = v.trim().replace(/\s+/g, ' ');
      return s.length > 38 ? s.slice(0, 38) + '…' : s;
    }
  }
  return '';
}

// ─── Inputs / outputs derivation

function basenameOf(s3Key) {
  if (!s3Key) return '';
  return String(s3Key).split('/').pop() || '';
}

/**
 * Build the list of input files visible for a design, deduped by basename.
 *
 * Today the backend tracks brdDocumentS3Key + dataMappingS3Key as the only
 * inputs (Phase 1 scope). When Phase 2 lands an `inputFiles[]` schema, the
 * tree picks that up by adding an extra branch here without touching the
 * call sites.
 */
function inputsForDesign(design) {
  // Prefer the orchestrator's listed `inputs[]` (presigned, includes
  // supporting files added via the New Project modal). Falls back to
  // the legacy two-key shape so the leaf rows still render before the
  // /design/{id} GET completes.
  if (Array.isArray(design?.inputs) && design.inputs.length) {
    const seen = new Set();
    const out = [];
    for (const f of design.inputs) {
      const name = (f.name || basenameOf(f.s3Key) || '').trim();
      if (!name) continue;
      const lower = name.toLowerCase();
      if (seen.has(lower)) continue;
      seen.add(lower);
      out.push({
        name,
        s3Key: f.s3Key,
        downloadUrl: f.downloadUrl || '',
        // The backend marks `missing: true` when the row references
        // an s3Key but HeadObject can't find the file. The tree row
        // shows a faded label + "drop a new file in chat to re-upload"
        // tooltip so the user knows why click/download is disabled.
        missing: !!f.missing,
      });
    }
    return out;
  }
  const seen = new Set();
  const out = [];
  const push = (key) => {
    const name = basenameOf(key);
    if (!name) return;
    const lower = name.toLowerCase();
    if (seen.has(lower)) return;
    seen.add(lower);
    out.push({ name, s3Key: key, downloadUrl: '' });
  };
  push(design?.brdDocumentS3Key);
  push(design?.dataMappingS3Key);
  return out;
}

/**
 * Group `design.outputs[]` (returned by the design-api Lambda's
 * handle_get_design) into stage buckets so the sidebar can render
 * Extraction / Design / QA / Documents / Misc as separate sub-nodes.
 *
 * Each output entry shape: { s3Key, stage, name, size, lastModified, downloadUrl }.
 * `stage` is the path segment after `outputs/` (extraction|design|qa|documents|misc).
 * Without this grouping, intermediate JSON artifacts like
 * `extraction/brd_extracted.json` and `design/segmentation_v1.json`
 * stay invisible — the user can't sanity-check what the agent saw
 * before downstream stages compound errors.
 */
const STAGE_LABELS = {
  extraction: 'Extraction',
  design: 'Design',
  qa: 'QA',
  documents: 'Documents',
  misc: 'Other',
};
const STAGE_ORDER = ['extraction', 'design', 'qa', 'documents', 'misc'];

// Stage outputs to surface in the project tree under DOCUMENTS. Empty
// set = the staged-output rows are completely hidden — the tree shows
// only the editable v1 entry, which is the canonical "open this in the
// canvas" affordance. The rendered HTML/DOCX exports live in S3 at
// outputs/documents/ but are reachable via the right-click → Export
// menu on the v1 row, not the tree. Re-add a stage here if you want
// it surfaced (e.g. `new Set(['documents'])`).
const VISIBLE_STAGES = new Set([]);

function stagedOutputsForDesign(design) {
  const groups = {};
  const list = Array.isArray(design?.outputs) ? design.outputs : [];
  for (const o of list) {
    const stage = o?.stage || 'misc';
    if (!VISIBLE_STAGES.has(stage)) continue;
    if (!groups[stage]) groups[stage] = [];
    groups[stage].push(o);
  }
  // Stable order: known visible stages first, then anything else
  // alphabetical (unreachable today — VISIBLE_STAGES is a closed set —
  // but kept defensive in case someone adds a new visible stage later).
  const known = STAGE_ORDER.filter((k) => groups[k]);
  const extras = Object.keys(groups).filter((k) => !STAGE_ORDER.includes(k)).sort();
  return [...known, ...extras].map((stage) => ({
    stage,
    label: STAGE_LABELS[stage] || stage.charAt(0).toUpperCase() + stage.slice(1),
    files: groups[stage].slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')),
  }));
}

/**
 * Build the list of output document versions from the documents map the
 * orchestrator returns. Falls back to a single-row list pinned at the
 * design's `currentVersion` when the per-version array isn't populated.
 */
function versionsForDesign(design) {
  const docs = design?.documents;
  if (Array.isArray(docs?.versions) && docs.versions.length) {
    return docs.versions.slice().sort((a, b) => Number(b.version) - Number(a.version));
  }
  // Fallback: synthesize a single row from the latest URL we have.
  const v = docs?.currentVersion || docs?.version || design?.currentVersion;
  if (!v && !docs?.html?.previewUrl) return [];
  return [{
    version: v || 1,
    htmlUrl: docs?.html?.previewUrl || '',
    docxUrl: docs?.docx?.downloadUrl || '',
    savedAt: docs?.savedAt || design?.updatedAt || null,
  }];
}

// ─── Shared menu close hook

function useMenuClose(isOpen, onClose) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onAway = () => onClose();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('mousedown', onAway);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onAway);
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);
}

// ─── Component

export default function ProjectTree({
  designs,
  activeDesignId,
  // Full design row for the active workflow (with inputs[]/outputs[]
  // populated by handle_get_design). When the parent reloads after a
  // chat upload, this prop changes and the sidebar tree picks up the
  // new file without waiting for the lazy designCache to expire.
  activeDesignFull,
  query,
  onAfterDelete,
  onAfterUpdate,
  onOpenInputFile,
  // Phase 3a-lite: parent is responsible for the "+ New workflow"
  // flow (it knows the user's email + can navigate to the new
  // workflow once created). The tree just signals the request.
  onNewWorkflow,
  // Quick-upload affordance on a workflow row (VS Code "new file"
  // style). Receives (designId, file). Parent uploads to that
  // workflow's inputs/ and refreshes the row so the file lands.
  onUploadInputFile,
  // Authenticated user identity — needed for KB uploads from the
  // project-edit modal. Without these, the prior version pulled from
  // the design-cache row which is often empty for fresh project shells,
  // so KB uploads silently no-oped.
  consultantId,
  consultantEmail,
  // Returns the live BlockNote editor blocks for the active design,
  // or null when the canvas hasn't mounted yet. Used by the version
  // row's right-click Export items so they work even before the
  // autosave debounce has written `pipeline.canvasS3Key` to the
  // design row — the live editor is the canonical state, the S3 key
  // is just a cached projection.
  getActiveDesignBlocks,
  isLoading = false,
}) {
  const navigate = useNavigate();
  // Per-design expansion: { [designId]: { open, inputs, outputs, designDoc, chat } }
  // Persisted in localStorage so the tree's open state survives a
  // NewChat → DesignView navigation. Without this, uploading a BRD
  // from chat causes the projects to "collapse" in the user's eyes
  // because we re-mount with empty state.
  const [expanded, setExpanded] = useState(() => {
    try { return JSON.parse(localStorage.getItem('project-tree-expanded') || '{}'); }
    catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem('project-tree-expanded', JSON.stringify(expanded)); } catch {}
  }, [expanded]);
  // Per-project collapse — keyed by projectId. Defaults to expanded;
  // user clicks the twist on the l1 row to collapse the whole group.
  const [collapsedProjects, setCollapsedProjects] = useState(() => {
    try { return JSON.parse(localStorage.getItem('project-tree-collapsed') || '{}'); }
    catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem('project-tree-collapsed', JSON.stringify(collapsedProjects)); } catch {}
  }, [collapsedProjects]);
  const toggleProject = useCallback((pid) => {
    if (!pid) return;
    // Absent key = collapsed (the mock's flat default), so the first
    // click must expand.
    setCollapsedProjects((prev) => ({ ...prev, [pid]: !(prev[pid] ?? true) }));
  }, []);
  // Lazy per-design payload cache — full design + chat count.
  const [designCache, setDesignCache] = useState({});

  // Per-surface optimistic overrides. Each rename surface (workflow
  // folder, document version row, canvas H1) is an independent DDB
  // attribute now; each one gets its own override map so an event from
  // one surface never patches the displayed value of another. The
  // `ajo:design-renamed` event carries `{ id, field, value }` and we
  // route to the right map by `field`. (Canvas renames update the
  // canvas H1 directly in DesignView; no leftrail patch is needed for
  // that field.)
  const [workflowOverrides, setWorkflowOverrides] = useState({});
  const [documentOverrides, setDocumentOverrides] = useState({});
  useEffect(() => {
    function onRenamed(e) {
      const detail = (e && e.detail) || {};
      const { id, field, value } = detail;
      if (!id || typeof value !== 'string') return;
      if (field === 'workflow') {
        setWorkflowOverrides((prev) => (
          prev[id] === value ? prev : { ...prev, [id]: value }
        ));
      } else if (field === 'document') {
        setDocumentOverrides((prev) => (
          prev[id] === value ? prev : { ...prev, [id]: value }
        ));
      }
      // field === 'canvas' is owned by DesignView's own state; no
      // leftrail row reads the canvas H1 title, so nothing to patch
      // here.
    }
    window.addEventListener('ajo:design-renamed', onRenamed);
    return () => window.removeEventListener('ajo:design-renamed', onRenamed);
  }, []);

  // Group by projectId — every row carries one (Phase 3a-lite). Each
  // group has at most one shell (status='project_shell', projectId===
  // designId) and zero-or-more workflows pointing to it. Legacy rows
  // without a projectId fall back to grouping by their own designId,
  // so each behaves as its own one-design project — preserves the
  // sidebar layout for pre-Phase-3a data.
  const projectGroups = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    // Dedupe by designId — keep only the latest version per workflow.
    // The backend LIST endpoint may return multiple rows per designId
    // (one per version) after a duplicate operation. The version history
    // renders from documents.versions[] on the single row, NOT as separate
    // workflow entries. Without this, the tree would render one workflow
    // row per version, breaking the layout and hiding other projects.
    const latestByDesignId = new Map();
    for (const d of designs || []) {
      const did = d.designId || d.workflowId || d.id;
      if (!did) continue;
      const existing = latestByDesignId.get(did);
      if (!existing || (d.version || 0) > (existing.version || 0)) {
        latestByDesignId.set(did, d);
      }
    }
    const deduped = Array.from(latestByDesignId.values());
    const groupsById = new Map();
    for (const d of deduped) {
      const pid = d.projectId || d.designId || d.workflowId;
      if (!pid) continue;
      if (!groupsById.has(pid)) groupsById.set(pid, []);
      groupsById.get(pid).push(d);
    }
    const out = [];
    for (const [pid, rows] of groupsById.entries()) {
      // Pick the project label: prefer the shell's designName, else
      // fall back to any row's getProjectName / designName. Only rows
      // explicitly stamped `status: 'project_shell'` count as shells —
      // a `r.designId === pid` heuristic mis-classifies BRD-upload
      // designs (which set projectId = self.designId) as their own
      // shell, leaving `workflows` empty and the leftrail unable to
      // render Inputs/Outputs sub-folders.
      const shell = rows.find((r) => r.status === 'project_shell');
      const projName = (
        (shell && (shell.designName || shell.name))
        || getProjectName(rows[0])
        || rows[0]?.designName
        || 'AJO'
      );
      // Workflows under the project = all rows that are NOT the shell.
      // For legacy data (no shell), every row is a "workflow"
      // (visually it just looks like a single-workflow project).
      const workflows = rows.filter((r) => r !== shell);
      // Search filter: keep the group if project name OR any of the
      // three workflow surfaces (workflow folder / document row /
      // canvas H1) matches. Each surface is independent now so search
      // has to look at all of them — a user who renamed only the
      // canvas H1 should still find the design by typing that name.
      if (q) {
        const projHit = projName.toLowerCase().includes(q);
        const wfHit = workflows.some((w) => (
          (getWorkflowTitle(w) || '').toLowerCase().includes(q)
          || (getDocumentTitle(w) || '').toLowerCase().includes(q)
          || (getCanvasTitle(w) || '').toLowerCase().includes(q)
        ));
        if (!projHit && !wfHit) continue;
      }
      // Latest activity across the group's rows — drives the recency
      // sort and the relative-time chip on the project row (per the
      // approved rail mock).
      const rowTs = (r) => {
        const v = Date.parse(r.updatedAt || r.lastModified || r.createdAt || '');
        return Number.isFinite(v) ? v : 0;
      };
      const lastTs = Math.max(0, ...rows.map(rowTs));
      out.push({ projectId: pid, projectName: projName, shell, workflows, lastTs });
    }
    out.sort((a, b) => b.lastTs - a.lastTs);
    return out;
  }, [designs, query]);

  // ── Rail sections state (per the approved sidebar mock) ──
  // Projects list is capped to the newest few; "All projects…" reveals
  // the rest. Starred projects come from localStorage (starred.js) and
  // stay in sync across pages via the service's window event.
  const PROJECTS_CAP = 6;
  const RECENTS_CAP = 5;
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [showAllRecents, setShowAllRecents] = useState(false);
  const [starredIds, setStarredIds] = useState(() => getStarredProjects());
  useEffect(() => {
    const sync = () => setStarredIds(getStarredProjects());
    window.addEventListener('ajo:starred-changed', sync);
    return () => window.removeEventListener('ajo:starred-changed', sync);
  }, []);

  // Recents — the newest workflows across every project, one layer deep.
  // Derived from projectGroups so it inherits the same dedupe.
  const recentDesigns = useMemo(() => {
    const rowTs = (r) => {
      const v = Date.parse(r.updatedAt || r.lastModified || r.createdAt || '');
      return Number.isFinite(v) ? v : 0;
    };
    return projectGroups
      .flatMap((g) => g.workflows)
      .map((w) => ({ row: w, ts: rowTs(w) }))
      .sort((a, b) => b.ts - a.ts);
  }, [projectGroups]);

  const ensureFullDesign = useCallback(async (did) => {
    if (!did || designCache[did]) return designCache[did];
    try {
      const full = await getDesign(did);
      setDesignCache((prev) => ({ ...prev, [did]: full }));
      return full;
    } catch (_) {
      return null;
    }
  }, [designCache]);

  // Auto-expand the active design (and its sub-groups) when DesignView
  // mounts. Without this, the user lands on a design page and sees only
  // the flat tree from listDesigns — they have to click a tiny chevron
  // to discover Inputs/Outputs/Chat. Mirrors the screenshot's behavior
  // where the open design is expanded by default.
  useEffect(() => {
    if (!activeDesignId) return;
    setExpanded((prev) => {
      // Don't clobber an explicit user collapse — only seed once.
      if (prev[activeDesignId]?.touched) return prev;
      return {
        ...prev,
        [activeDesignId]: {
          open: true,
          inputs: true,
          outputs: true,
          designDoc: true,
          touched: true,
        },
      };
    });
    // Also ensure the parent project group is EXPANDED so the
    // workflow row itself renders. Without this, a stale
    // localStorage entry that flipped the project to collapsed
    // would hide the active workflow + its INPUTS/OUTPUTS folders
    // (user-reported: "creating a project doesn't show workflow /
    // documents folders / v1 automatically"). Auto-expanding the
    // workflow alone doesn't help if its l1 project parent is
    // collapsed — the l2 row never renders.
    const activeRow = (designs || []).find((d) => (
      d.designId === activeDesignId
      || d.workflowId === activeDesignId
    ));
    const parentPid = activeRow?.projectId || activeDesignId;
    // Explicit `false` — with collapse-by-default, an absent key means
    // collapsed, so the old "only when truthy" guard would leave the
    // active project's group hidden.
    setCollapsedProjects((prev) => (
      prev[parentPid] === false ? prev : { ...prev, [parentPid]: false }
    ));
    ensureFullDesign(activeDesignId);
  }, [activeDesignId, designs, ensureFullDesign]);

  const toggleDesign = useCallback((did) => {
    setExpanded((prev) => {
      const cur = prev[did] || {};
      return {
        ...prev,
        [did]: {
          ...cur,
          // Seed sub-group state on first user-driven open so they're
          // visible immediately, matching the auto-expand effect above.
          inputs: cur.inputs ?? !cur.open ? true : cur.inputs,
          outputs: cur.outputs ?? !cur.open ? true : cur.outputs,
          designDoc: cur.designDoc ?? !cur.open ? true : cur.designDoc,
          open: !cur.open,
          touched: true,
        },
      };
    });
    if (!designCache[did] && !(expanded[did]?.open)) {
      ensureFullDesign(did);
    }
  }, [designCache, expanded, ensureFullDesign]);

  const toggleSection = useCallback((did, section) => {
    setExpanded((prev) => {
      const cur = prev[did] || {};
      return { ...prev, [did]: { ...cur, [section]: !cur[section], touched: true } };
    });
  }, []);

  const handleDownload = useCallback(async (did) => {
    try {
      const full = await ensureFullDesign(did);
      const url = full?.documents?.docx?.downloadUrl
        || full?.documents?.html?.downloadUrl
        || full?.documents?.json?.downloadUrl;
      if (url) window.open(url, '_blank', 'noopener');
    } catch (_) {}
  }, [ensureFullDesign]);

  // ─── Settings modal state

  const [settingsFor, setSettingsFor] = useState(null);
  const [settingsBusy, setSettingsBusy] = useState(false);

  const openSettings = useCallback((did, dtitle) => {
    setSettingsFor({ designId: did, name: dtitle });
  }, []);

  // Shared confirm + delete logic reused by both the modal Delete button
  // and the project/workflow context menu Delete items.
  const confirmAndDelete = useCallback(async (designId, displayName) => {
    const ok = await confirmDialog({
      title: 'Delete workflow',
      message: `Delete "${displayName}"? This removes the design row, chat history, and any uploaded inputs/outputs. Cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteDesign(designId);
      onAfterDelete?.(designId);
    } catch (e) {
      console.error('delete failed', e);
      await alertDialog({
        title: 'Delete failed',
        message: e?.response?.data?.error || e?.message || 'Unknown error',
      });
    }
  }, [onAfterDelete]);

  const handleSettingsUpdate = useCallback(async ({ name: newName, customerName: newCustomerName, kbFiles }) => {
    if (!settingsFor) return;
    setSettingsBusy(true);
    try {
      // Collect updates.
      const updates = {};
      if ((newName || '').trim() && newName.trim() !== settingsFor.name) {
        updates.designName = newName.trim().slice(0, 200);
      }
      if ((newCustomerName || '').trim()) {
        updates.customerName = newCustomerName.trim().slice(0, 200);
      }
      // Apply updates if any.
      if (Object.keys(updates).length > 0) {
        await updateDesign(settingsFor.designId, updates);
        setDesignCache((prev) => {
          const cur = prev[settingsFor.designId] || {};
          return { ...prev, [settingsFor.designId]: { ...cur, ...updates, name: updates.designName || cur.name } };
        });
        onAfterUpdate?.(settingsFor.designId, { ...updates, name: updates.designName || settingsFor.name });
      }
      // Upload any newly-attached KB files (consultant-level). Source
      // identity from the auth context the parent passed in — the prior
      // implementation pulled from the cached design row, which is
      // empty for fresh project shells, so uploads silently no-oped.
      const cached = designCache[settingsFor.designId] || {};
      const cid = consultantId || cached.consultantId;
      const email = consultantEmail || cached.consultantEmail || cid;
      if (Array.isArray(kbFiles) && kbFiles.length) {
        if (!cid || !email) {
          await alertDialog({
            title: 'KB upload skipped',
            message: 'Could not resolve your identity. Sign out and back in, then retry.',
          });
        } else {
          const failures = [];
          for (const f of kbFiles) {
            try {
              await uploadKnowledgeBaseFile(f, cid, email);
            } catch (e) {
              console.warn('KB upload failed:', f.name, e);
              failures.push(`${f.name}: ${e?.response?.data?.error || e?.message || 'unknown error'}`);
            }
          }
          if (failures.length) {
            await alertDialog({
              title: 'Some KB files failed',
              message: failures.join('\n'),
            });
          }
        }
      }
    } finally {
      setSettingsBusy(false);
      setSettingsFor(null);
    }
  }, [settingsFor, designCache, consultantId, consultantEmail, onAfterUpdate]);

  const handleSettingsDelete = useCallback(async () => {
    if (!settingsFor) return;
    setSettingsBusy(true);
    try {
      await confirmAndDelete(settingsFor.designId, settingsFor.name);
    } finally {
      setSettingsBusy(false);
      setSettingsFor(null);
    }
  }, [settingsFor, confirmAndDelete]);

  // ─── File context menu

  const [fileMenu, setFileMenu] = useState(null); // {x, y, file, deletable, onDelete, designId, isInput}
  const closeFileMenu = useCallback(() => setFileMenu(null), []);
  useMenuClose(!!fileMenu, closeFileMenu);

  const openFileMenu = useCallback((e, file, opts = {}) => {
    e.preventDefault();
    e.stopPropagation();
    setFileMenu({
      x: e.clientX,
      y: e.clientY,
      file,
      designId: opts.designId,
      deletable: !!opts.deletable,
      renameable: !!opts.renameable,
      onDelete: opts.onDelete,
      onRename: opts.onRename,
    });
  }, []);

  // ─── Project context menu

  const [projectMenu, setProjectMenu] = useState(null); // {x, y, projectId, projectName, shellDesignId}
  const closeProjectMenu = useCallback(() => setProjectMenu(null), []);
  useMenuClose(!!projectMenu, closeProjectMenu);

  const openProjectMenu = useCallback((e, projectId, projectName, shellDesignId) => {
    e.preventDefault();
    e.stopPropagation();
    setProjectMenu({ x: e.clientX, y: e.clientY, projectId, projectName, shellDesignId });
  }, []);

  // ─── Workflow context menu

  const [workflowMenu, setWorkflowMenu] = useState(null); // {x, y, designId, name}
  const closeWorkflowMenu = useCallback(() => setWorkflowMenu(null), []);
  useMenuClose(!!workflowMenu, closeWorkflowMenu);

  const openWorkflowMenu = useCallback((e, designId, name) => {
    e.preventDefault();
    e.stopPropagation();
    setWorkflowMenu({ x: e.clientX, y: e.clientY, designId, name });
  }, []);

  // ─── Version context menu (right-click on a v1/v2 row)

  const [versionMenu, setVersionMenu] = useState(null);
  const closeVersionMenu = useCallback(() => setVersionMenu(null), []);
  useMenuClose(!!versionMenu, closeVersionMenu);
  const openVersionMenu = useCallback((e, payload) => {
    e.preventDefault();
    e.stopPropagation();
    setVersionMenu({ x: e.clientX, y: e.clientY, ...payload });
  }, []);

  // ─── Inline workflow rename
  // Set { designId, value } when the user picks Rename from the workflow
  // ⋯ menu. The l2 row renders an <input> instead of the label for that
  // designId. Commit on Enter / blur, cancel on Escape.
  const [renamingWorkflow, setRenamingWorkflow] = useState(null);

  const commitRename = useCallback(async () => {
    const target = renamingWorkflow;
    if (!target) return;
    const trimmed = (target.value || '').trim().slice(0, 200);
    setRenamingWorkflow(null);
    if (!trimmed || trimmed === target.original) return;
    try {
      // Workflow rename writes ONLY workflowName. The document version
      // row and the canvas H1 each have their own attributes and fall
      // back to designName independently — renaming the workflow folder
      // doesn't touch them.
      await updateDesign(target.designId, { workflowName: trimmed });
      onAfterUpdate?.(target.designId, { workflowName: trimmed });
      window.dispatchEvent(new CustomEvent('ajo:design-renamed', {
        detail: { id: target.designId, field: 'workflow', value: trimmed },
      }));
    } catch (err) {
      await alertDialog({
        title: 'Rename failed',
        message: err?.message || 'unknown error',
      });
    }
  }, [renamingWorkflow, onAfterUpdate]);

  // ─── Inline project rename (l1 row). Same shape as workflow rename
  // but writes to the shell row's designName via updateDesign.
  const [renamingProject, setRenamingProject] = useState(null);

  const commitProjectRename = useCallback(async () => {
    const target = renamingProject;
    if (!target) return;
    const trimmed = (target.value || '').trim().slice(0, 200);
    setRenamingProject(null);
    if (!trimmed || trimmed === target.original) return;
    try {
      await updateDesign(target.shellDesignId, { designName: trimmed });
      onAfterUpdate?.(target.shellDesignId, { designName: trimmed, name: trimmed });
      window.dispatchEvent(new CustomEvent('ajo:design-renamed', {
        detail: { id: target.shellDesignId, field: 'project', value: trimmed },
      }));
    } catch (err) {
      await alertDialog({
        title: 'Rename failed',
        message: err?.message || 'unknown error',
      });
    }
  }, [renamingProject, onAfterUpdate]);

  // ─── Inline document rename — separate from workflow rename. The
  // document name is stored at design.documentName so renaming a doc
  // doesn't mutate the workflow label users see at the l2 row.
  const [renamingDoc, setRenamingDoc] = useState(null);

  const commitDocRename = useCallback(async () => {
    const target = renamingDoc;
    if (!target) return;
    const trimmed = (target.value || '').trim().slice(0, 200);
    setRenamingDoc(null);
    if (!trimmed || trimmed === target.original) return;
    try {
      // Document rename writes ONLY documentName. The workflow folder
      // row and the canvas H1 stay anchored on their own attributes (or
      // their designName fallback). Each surface is independent.
      await updateDesign(target.designId, { documentName: trimmed });
      setDesignCache((prev) => {
        const cached = prev[target.designId];
        if (!cached) return prev;
        return {
          ...prev,
          [target.designId]: { ...cached, documentName: trimmed },
        };
      });
      onAfterUpdate?.(target.designId, { documentName: trimmed });
      window.dispatchEvent(new CustomEvent('ajo:design-renamed', {
        detail: { id: target.designId, field: 'document', value: trimmed },
      }));
    } catch (err) {
      await alertDialog({
        title: 'Rename failed',
        message: err?.response?.data?.error || err?.message || 'unknown error',
      });
    }
  }, [renamingDoc, onAfterUpdate]);

  // ─── Inline file rename (l4 input/output row). Calls the design-api
  // /files/rename endpoint which does an S3 CopyObject + DeleteObject
  // under the same prefix-scoping rules as delete.
  const [renamingFile, setRenamingFile] = useState(null);

  // Transient toast for "Copy link" feedback. The version-row variant
  // copies the in-app design URL (Okta-gated, no expiration); the
  // file-row variant copies a pre-signed S3 URL that expires in 1h.
  // The toast spells out the difference so users don't paste the wrong
  // thing into Slack — that's the feedback that prompted this affordance.
  const [linkCopiedMsg, setLinkCopiedMsg] = useState('');
  useEffect(() => {
    if (!linkCopiedMsg) return undefined;
    const t = setTimeout(() => setLinkCopiedMsg(''), 4000);
    return () => clearTimeout(t);
  }, [linkCopiedMsg]);
  const copyToClipboard = useCallback(async (text, toastMsg) => {
    try {
      await navigator.clipboard.writeText(text);
      setLinkCopiedMsg(toastMsg);
    } catch {
      setLinkCopiedMsg('Copy failed — your browser blocked clipboard access.');
    }
  }, []);

  const commitFileRename = useCallback(async () => {
    const target = renamingFile;
    if (!target) return;
    const trimmed = (target.value || '').trim().slice(0, 200);
    setRenamingFile(null);
    if (!trimmed || trimmed === target.original) return;
    try {
      await renameWorkflowFile(target.s3Key, trimmed);
      // Drop the cached design row so the next expand/refresh picks
      // up the renamed file with a fresh presigned URL.
      setDesignCache((prev) => {
        const next = { ...prev };
        delete next[target.designId];
        return next;
      });
      ensureFullDesign(target.designId);
    } catch (err) {
      await alertDialog({
        title: 'Rename failed',
        message: err?.response?.data?.error || err?.message || 'unknown error',
      });
    }
  }, [renamingFile, ensureFullDesign]);

  if (projectGroups.length === 0) {
    if (isLoading) {
      return <ProjectTreeSkeleton />;
    }
    return (
      <div className="tree-row l1">
        <span className="twist empty">·</span>
        <span className="glyph">▣</span>
        <span className="tree-label">No projects yet</span>
        <span className="tree-meta">0</span>
      </div>
    );
  }

  // Flatten the project header in two cases:
  //   1. Only one project group AND its name is the generic "AJO" fallback
  //      (the workspace label is redundant noise above a single design)
  //   2. The project has a single design AND `designName === projectName`
  //      (the modal flow puts the user's typed name on both sides — no
  //      reason to show "test (1) > test v1 > …", just show the design)
  const showProjectHeaderFor = (proj, ds) => {
    if (proj === 'AJO') return false;
    if (ds.length === 1) {
      const only = ds[0];
      // Flatten when the workflow's display label matches the project
      // name. Use getWorkflowTitle so post-rename divergence (project
      // kept its old name but workflow got renamed, or vice versa)
      // un-flattens correctly.
      const dtitle = getWorkflowTitle(only) || '';
      if (dtitle && dtitle.trim().toLowerCase() === proj.trim().toLowerCase()) {
        return false;
      }
    }
    return true;
  };

  // Section slices (mock: capped PROJECTS + All projects…, then STARRED,
  // then RECENTS). While searching, the filtered project groups are the
  // whole story — the cap and the extra sections get out of the way.
  const searching = !!(query || '').trim();
  let visibleGroups = (searching || showAllProjects)
    ? projectGroups
    : projectGroups.slice(0, PROJECTS_CAP);
  // The active design's project must never hide behind the cap — opening
  // an old design from a deep link would otherwise leave the rail with no
  // visible active row.
  if (!searching && !showAllProjects && activeDesignId) {
    const activeGroup = projectGroups.find((g) => (
      g.shell?.designId === activeDesignId
      || g.workflows.some((w) => (w.designId || w.workflowId || w.id) === activeDesignId)
    ));
    if (activeGroup && !visibleGroups.includes(activeGroup)) {
      visibleGroups = [...visibleGroups, activeGroup];
    }
  }
  const hiddenProjectCount = projectGroups.length - visibleGroups.length;
  const starredGroups = searching
    ? []
    : projectGroups.filter((g) => starredIds.includes(g.projectId));
  const visibleRecents = searching
    ? []
    : recentDesigns.slice(0, showAllRecents ? RECENTS_CAP * 3 : RECENTS_CAP);
  // Reveal a project hidden behind the cap (starred-row click).
  const revealProject = (pid) => {
    const idx = projectGroups.findIndex((g) => g.projectId === pid);
    if (idx >= PROJECTS_CAP) setShowAllProjects(true);
    setCollapsedProjects((prev) => ({ ...prev, [pid]: false }));
  };

  return (
    <>
      {visibleGroups.map(({ projectId, projectName, shell, workflows, lastTs }) => {
        const settingsTarget = shell || workflows[0];
        const settingsDid = settingsTarget?.designId;
        // Collapsed by default (flat rows per the mock); absent key =
        // collapsed. While searching, matched groups auto-expand so a
        // workflow-name hit isn't hidden under a collapsed project row.
        const isCollapsed = searching ? false : (collapsedProjects[projectId] ?? true);
        // Extract customer name from shell or first workflow.
        const customerName = shell?.customerName || workflows[0]?.customerName || '';
        return (
        <React.Fragment key={projectId}>
          <div
            className="tree-row l1"
            onClick={() => toggleProject(projectId)}
            onContextMenu={(e) => openProjectMenu(e, projectId, projectName, settingsDid)}
          >
            <span
              className="twist clickable"
              onClick={(e) => { e.stopPropagation(); toggleProject(projectId); }}
            >{isCollapsed ? '›' : '⌄'}</span>
            {/* Colored avatar circle removed — minimal tree (just the
                expand twist + name). */}
            {renamingProject?.projectId === projectId ? (
              <input
                autoFocus
                className="tree-rename-input"
                value={renamingProject.value}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) =>
                  setRenamingProject((prev) =>
                    prev && prev.projectId === projectId
                      ? { ...prev, value: e.target.value }
                      : prev
                  )
                }
                onBlur={commitProjectRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitProjectRename();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setRenamingProject(null);
                  }
                }}
              />
            ) : (
              // Single-line row — a per-row customer subtitle doubled every
              // row's height and made the rail unreadable. The customer
              // lives in the tooltip; editing stays in the project modal.
              <span
                className="tree-label"
                title={customerName ? `${projectName} — ${customerName}` : projectName}
              >{projectName}</span>
            )}
            {lastTs > 0 && (
              <span className="tree-meta">{formatRelativeCompact(lastTs)}</span>
            )}
            <span className="tree-row-actions" onClick={(e) => e.stopPropagation()}>
              <button
                title="Project menu"
                className="tree-kebab"
                onClick={(e) => openProjectMenu(e, projectId, projectName, settingsDid)}
              >⋯</button>
            </span>
          </div>

          {!isCollapsed && workflows.map((d) => {
            const did = d.designId || d.workflowId || d.id;
            // L2 workflow folder row: optimistic override (just-renamed)
            // beats the row's persisted workflowName, which beats the
            // creation-default designName fallback inside getWorkflowTitle.
            // We deliberately do NOT read d.title here — that's the
            // canvas H1 and must stay independent.
            const dtitle = workflowOverrides[did]
              || getWorkflowTitle(d) || 'Untitled';
            const isActive = did === activeDesignId;
            const exp = expanded[did] || {};
            // For the active workflow, prefer the parent-supplied full
            // row over the lazy cache so chat-driven uploads / reloads
            // surface immediately. Other workflows still use the cache.
            const full = (isActive && activeDesignFull && (
              activeDesignFull.designId === did
              || activeDesignFull.workflowId === did
              || activeDesignFull.id === did
            )) ? activeDesignFull : (designCache[did] || d);
            const inputs = inputsForDesign(full);
            const versions = versionsForDesign(full);
            const stagedOutputs = stagedOutputsForDesign(full);
            const stagedFileCount = stagedOutputs.reduce((n, g) => n + g.files.length, 0);
            // The DOCUMENTS section now ALWAYS shows exactly one v1 row
            // (the editable canvas entry). The legacy "Design Document"
            // sub-folder + Preview.html branches were dropped — they
            // came from the pre-AgentCore CDK pipeline. `hasAnyOutputs`
            // and `outputsCount` are kept for any callers / styling that
            // still reference them but no longer drive conditional
            // rendering of the tree shape.
            const hasAnyOutputs = true;

            return (
              <React.Fragment key={did}>
                <div
                  className={`tree-row l2${isActive ? ' active' : ''}`}
                  onClick={() => did && navigate(`/design/${did}`)}
                  onContextMenu={(e) => openWorkflowMenu(e, did, dtitle)}
                >
                  <span
                    className="twist clickable"
                    onClick={(e) => { e.stopPropagation(); toggleDesign(did); }}
                  >
                    {exp.open ? '⌄' : '›'}
                  </span>
                  <span className="tree-folder-icon">
                    <FolderIcon expanded={exp.open} />
                  </span>
                  <span className="tree-label-stack">
                    {renamingWorkflow?.designId === did ? (
                      <input
                        autoFocus
                        className="tree-rename-input"
                        value={renamingWorkflow.value}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          setRenamingWorkflow((prev) =>
                            prev && prev.designId === did
                              ? { ...prev, value: e.target.value }
                              : prev
                          )
                        }
                        onBlur={commitRename}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitRename();
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            setRenamingWorkflow(null);
                          }
                        }}
                      />
                    ) : (
                      <span className="tree-label">{dtitle}</span>
                    )}
                  </span>
                </div>

                {exp.open && (
                  <>
                    {/* INPUTS — always rendered so a fresh workflow shows
                     * the same folder layout as a populated one. Without
                     * this, a new project looks "empty" to the user even
                     * though it's just waiting for an upload. (User
                     * feedback: "create folders inside the project when
                     * a new project is created.") */}
                    {true && (<>
                    <div
                      className="tree-row l3 clickable"
                      onClick={() => toggleSection(did, 'inputs')}
                    >
                      <span className="twist">{exp.inputs ? '⌄' : '›'}</span>
                      <span className="tree-section-label">Inputs</span>
                      {inputs.length > 0 && (
                        <span className="tree-meta">{inputs.length}</span>
                      )}
                    </div>
                    {exp.inputs && inputs.length === 0 && (
                      <div className="tree-row l4 tree-row-empty">
                        <span className="twist empty">·</span>
                        <span className="tree-label tree-label-muted">
                          Drop any document into chat — BRDs, data mappings, supporting files.
                        </span>
                      </div>
                    )}
                    {exp.inputs && inputs.map((f) => {
                      const isRenaming = renamingFile?.s3Key === f.s3Key;
                      return (
                      <div
                        className={`tree-row l4${f.missing ? ' tree-row-missing' : ''}`}
                        key={f.s3Key}
                        title={
                          f.missing
                            ? `${f.name} — file no longer in S3. Drop a new copy in the chat composer to re-attach.`
                            : `Preview ${f.name} — right-click for options`
                        }
                        onClick={async () => {
                          if (isRenaming) return;
                          if (f.missing) return;
                          // s3Key is the clickability gate. Resolve the URL
                          // lazily on demand — the backend may no longer
                          // pre-populate downloadUrl in inputs[].
                          if (onOpenInputFile && f.s3Key) {
                            onOpenInputFile({ ...f, designId: did });
                          } else if (f.s3Key) {
                            // Open blank window synchronously to preserve the
                            // user-gesture context (popup blockers), then set
                            // location after resolving the URL.
                            const newWindow = window.open('', '_blank', 'noopener');
                            const url = await resolveFileUrl(did, f);
                            if (url && newWindow) {
                              newWindow.location.href = url;
                            } else if (newWindow) {
                              newWindow.close();
                            }
                          }
                        }}
                        onContextMenu={(e) => openFileMenu(e, f, {
                          designId: did,
                          deletable: !!f.s3Key && !f.missing,
                          renameable: !!f.s3Key && !f.missing,
                          onRename: (file) => setRenamingFile({
                            s3Key: file.s3Key,
                            designId: did,
                            value: file.name || '',
                            original: file.name || '',
                          }),
                          onDelete: async (file) => {
                            try {
                              await deleteWorkflowFile(file.s3Key);
                              setDesignCache((prev) => {
                                const cached = prev[did];
                                if (!cached) return prev;
                                const nextInputs = (cached.inputs || []).filter(
                                  (i) => i.s3Key !== file.s3Key
                                );
                                return { ...prev, [did]: { ...cached, inputs: nextInputs } };
                              });
                            } catch (err) {
                              await alertDialog({
                                title: 'Delete failed',
                                message: err?.response?.data?.error || err?.message || 'Unknown error',
                              });
                            }
                          },
                        })}
                        style={{ cursor: f.s3Key && !f.missing ? 'pointer' : 'default' }}
                      >
                        <span className="twist empty">·</span>
                        {isRenaming ? (
                          <input
                            autoFocus
                            className="tree-rename-input"
                            value={renamingFile.value}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              setRenamingFile((prev) =>
                                prev && prev.s3Key === f.s3Key
                                  ? { ...prev, value: e.target.value }
                                  : prev
                              )
                            }
                            onBlur={commitFileRename}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                commitFileRename();
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                setRenamingFile(null);
                              }
                            }}
                          />
                        ) : (
                          <span className="tree-label">{f.name}{f.missing ? ' (missing)' : ''}</span>
                        )}
                      </div>
                      );
                    })}
                    </>)}

                    {/* Staged outputs (currently empty since VISIBLE_STAGES=[])
                     * still render gated by exp.outputs if ever re-enabled. */}
                    {exp.outputs && stagedOutputs.length > 0 && (
                      <>
                        {stagedOutputs.map((g) => {
                          const sectionKey = `outputs.${g.stage}`;
                          const sectionOpen = exp[sectionKey];
                          return (
                            <React.Fragment key={`${did}-stage-${g.stage}`}>
                              <div
                                className="tree-row l4 clickable"
                                onClick={() => toggleSection(did, sectionKey)}
                              >
                                <span className="twist">{sectionOpen ? '⌄' : '›'}</span>
                                <span className="tree-label">{g.label}</span>
                                <span className="tree-meta">{g.files.length}</span>
                              </div>
                              {sectionOpen && g.files.map((f) => {
                                const isRenaming = renamingFile?.s3Key === f.s3Key;
                                return (
                                <div
                                  className="tree-row l4 indented"
                                  key={f.s3Key}
                                  title={`Preview ${f.name} — right-click for options`}
                                  onClick={async () => {
                                    if (isRenaming) return;
                                    if (onOpenInputFile && f.s3Key) {
                                      onOpenInputFile({ ...f, designId: did });
                                    } else if (f.s3Key) {
                                      const newWindow = window.open('', '_blank', 'noopener');
                                      const url = await resolveFileUrl(did, f);
                                      if (url && newWindow) {
                                        newWindow.location.href = url;
                                      } else if (newWindow) {
                                        newWindow.close();
                                      }
                                    }
                                  }}
                                  onContextMenu={(e) => openFileMenu(e, f, {
                                    designId: did,
                                    deletable: !!f.s3Key,
                                    renameable: !!f.s3Key,
                                    onRename: (file) => setRenamingFile({
                                      s3Key: file.s3Key,
                                      designId: did,
                                      value: file.name || '',
                                      original: file.name || '',
                                    }),
                                    onDelete: async (file) => {
                                      try {
                                        await deleteWorkflowFile(file.s3Key);
                                        setDesignCache((prev) => {
                                          const cached = prev[did];
                                          if (!cached) return prev;
                                          const nextOutputs = (cached.outputs || []).filter(
                                            (o) => o.s3Key !== file.s3Key
                                          );
                                          return { ...prev, [did]: { ...cached, outputs: nextOutputs } };
                                        });
                                      } catch (err) {
                                        await alertDialog({
                                          title: 'Delete failed',
                                          message: err?.response?.data?.error || err?.message || 'Unknown error',
                                        });
                                      }
                                    },
                                  })}
                                  style={{ cursor: f.missing ? 'not-allowed' : (f.s3Key ? 'pointer' : 'default') }}
                                >
                                  <span className="twist empty">·</span>
                                  {isRenaming ? (
                                    <input
                                      autoFocus
                                      className="tree-rename-input"
                                      value={renamingFile.value}
                                      onClick={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onChange={(e) =>
                                        setRenamingFile((prev) =>
                                          prev && prev.s3Key === f.s3Key
                                            ? { ...prev, value: e.target.value }
                                            : prev
                                        )
                                      }
                                      onBlur={commitFileRename}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          commitFileRename();
                                        } else if (e.key === 'Escape') {
                                          e.preventDefault();
                                          setRenamingFile(null);
                                        }
                                      }}
                                    />
                                  ) : (
                                    <span className="tree-label">{f.name}</span>
                                  )}
                                </div>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </>
                    )}
                    {/* Version rows — one per version, rendered at l3 (parallel
                     * to Inputs section header), directly under the workflow,
                     * always visible when the workflow is expanded. Rendered
                     * HTML/DOCX exports are reachable via the right-click
                     * → Export menu, not the tree. */}
                    {versions.map((ver) => {
                      // Optimistic override beats persisted documentName,
                      // which beats designName fallback inside
                      // getDocumentTitle. Independent of the workflow
                      // folder label (dtitle above) and the canvas H1 —
                      // each rename only writes its own attribute.
                      const docLabel = documentOverrides[did]
                        || getDocumentTitle(full)
                        || 'Untitled document';
                      const isRenaming = renamingDoc?.designId === did;
                      const versionNum = ver.version || 1;
                      const isDraft = !ver.htmlUrl && !ver.docxUrl;
                      return (
                      <div
                        key={`${did}-v${versionNum}`}
                        className="tree-row l3 version-row"
                        onClick={() => {
                          if (isRenaming) return;
                          if (onOpenInputFile) onOpenInputFile(null);
                          if (did) {
                            navigate(
                              isDraft
                                ? `/design/${did}`
                                : `/design/${did}?v=${versionNum}`
                            );
                          }
                        }}
                        onContextMenu={(e) => openVersionMenu(e, {
                          designId: did,
                          workflowName: dtitle,
                          documentName: docLabel,
                          isDraft,
                          version: ver,
                          isOnlyVersion: versions.length === 1,
                          blocksUrl: full?.documents?.blocks?.previewUrl || '',
                          isActive: did === activeDesignId,
                          // Creation/edit info for the export title block.
                          createdAt: full?.createdAt || '',
                          updatedAt: full?.updatedAt || '',
                          ownerEmail: full?.consultantId || full?.consultantEmail || '',
                          // Cover fields for DOCX export
                          customerName: full?.customerName || '',
                          versionNumber: versionNum,
                        })}
                        title={
                          isDraft
                            ? `Open ${docLabel} — saves as v${versionNum} once exported`
                            : `Open ${docLabel} v${versionNum} — ${relativeTime(ver.savedAt)}`
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="twist empty">·</span>
                        <span className="version-pill">v{versionNum}</span>
                        {isRenaming ? (
                          <input
                            autoFocus
                            className="tree-rename-input"
                            value={renamingDoc.value}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              setRenamingDoc((prev) =>
                                prev && prev.designId === did
                                  ? { ...prev, value: e.target.value }
                                  : prev
                              )
                            }
                            onBlur={commitDocRename}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                commitDocRename();
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                setRenamingDoc(null);
                              }
                            }}
                          />
                        ) : (
                          <span className="tree-label">{docLabel}</span>
                        )}
                        <span className="tree-meta">
                          {isDraft ? 'draft' : relativeTime(ver.savedAt)}
                        </span>
                      </div>
                      );
                    })}
                    {/* No standalone Chat row — chat IS the design view; the
                     * row is opened by clicking the design title above. */}
                  </>
                )}
              </React.Fragment>
            );
          })}
        </React.Fragment>
        );
      })}

      {!searching && (hiddenProjectCount > 0 || showAllProjects) && (
        <button
          type="button"
          className="tree-showall"
          onClick={() => setShowAllProjects((v) => !v)}
        >
          {showAllProjects ? 'Show less' : `All projects… (${projectGroups.length})`}
        </button>
      )}

      {starredGroups.length > 0 && (
        <>
          <div className="rail-section-head tree-section-head">
            <span className="label">Starred</span>
          </div>
          {starredGroups.map((g) => (
            <div
              key={`star-${g.projectId}`}
              className="tree-row l1"
              onClick={() => revealProject(g.projectId)}
              title={g.projectName}
            >
              <span className="twist empty">·</span>
              <span className="glyph tree-star">★</span>
              <span className="tree-label">{g.projectName}</span>
            </div>
          ))}
        </>
      )}

      {visibleRecents.length > 0 && (
        <>
          <div className="rail-section-head tree-section-head">
            <span className="label">Recents</span>
          </div>
          {visibleRecents.map(({ row, ts }) => {
            const did = row.designId || row.workflowId || row.id;
            const label = workflowOverrides[did] || getWorkflowTitle(row) || 'Untitled';
            return (
              <div
                key={`recent-${did}`}
                className={`tree-row l1${did === activeDesignId ? ' active' : ''}`}
                onClick={() => navigate(`/design/${did}`)}
                title={label}
              >
                <span className="twist empty">·</span>
                <span className="tree-label">{label}</span>
                {ts > 0 && <span className="tree-meta">{formatRelativeCompact(ts)}</span>}
              </div>
            );
          })}
          {(recentDesigns.length > RECENTS_CAP) && (
            <button
              type="button"
              className="tree-showall"
              onClick={() => setShowAllRecents((v) => !v)}
            >
              {showAllRecents ? 'Show less' : 'Show all…'}
            </button>
          )}
        </>
      )}

      <NewProjectModal
        open={!!settingsFor}
        mode="edit"
        initialName={settingsFor?.name || ''}
        initialCustomerName={settingsFor?.customerName || (settingsFor?.designId && (designCache[settingsFor.designId]?.customerName || ''))}
        designs={designs}
        busy={settingsBusy}
        onClose={() => !settingsBusy && setSettingsFor(null)}
        onUpdate={handleSettingsUpdate}
        onDelete={handleSettingsDelete}
      />

      {/* Version right-click context menu (Export / Share / Rename / Delete).
       * Used for both real version rows AND the synthetic v1 (draft) row.
       * For drafts: Export is disabled (no rendered doc yet), Rename
       * renames the parent workflow (the draft inherits its title),
       * Delete deletes the workflow itself. */}
      {versionMenu && (() => {
        const v = versionMenu.version;
        const did = versionMenu.designId;
        const isOnly = versionMenu.isOnlyVersion;
        const isDraft = !!versionMenu.isDraft;
        const wfName = versionMenu.workflowName || '';
        const open = (u) => { if (u) window.open(u, '_blank', 'noopener'); };
        const keyFromUrl = (u) => {
          if (!u) return '';
          try {
            const p = new URL(u);
            return decodeURIComponent(p.pathname.replace(/^\//, ''));
          } catch {
            return '';
          }
        };
        return (
          <div
            className="tree-context-menu"
            style={{ left: versionMenu.x, top: versionMenu.y }}
            onMouseDown={(e) => e.stopPropagation()}
            role="menu"
          >
            {(() => {
              // Native client-side exports (DOCX/HTML/JSON) work from
              // EITHER the live BlockNote editor's blocks (when the
              // user is right-clicking the active design) OR the
              // design row's persisted `documents.blocks.previewUrl`
              // (post-autosave). The live blocks are the canonical
              // source — the S3 URL is just a cached projection and
              // can lag the streamed-in content by 1.5s while the
              // autosave debounces.
              const blocksUrl = versionMenu.blocksUrl || '';
              const isActive = !!versionMenu.isActive;
              const liveBlocks = isActive
                ? (typeof getActiveDesignBlocks === 'function'
                    ? (getActiveDesignBlocks() || null)
                    : null)
                : null;
              const hasLive = Array.isArray(liveBlocks) && liveBlocks.length > 0;
              const safeName = (versionMenu.documentName || versionMenu.workflowName || 'design')
                .replace(/[^A-Za-z0-9 _.-]+/g, '').trim() || 'design';
              const runExport = async (format) => {
                setVersionMenu(null);
                // Re-snapshot live blocks at click-time so any edits
                // between right-click and click land in the export.
                const liveAtClick = isActive && typeof getActiveDesignBlocks === 'function'
                  ? (getActiveDesignBlocks() || null)
                  : null;
                const useLive = Array.isArray(liveAtClick) && liveAtClick.length > 0;
                // Pretty title for the export — prefer the workflow's
                // human name over the document filename (which is often
                // an unhelpful "Untitled document"). Falls back through
                // the same chain ProjectTree uses for the on-screen
                // breadcrumb so the exported doc says the same thing
                // as the canvas.
                const titleParts = [];
                if (versionMenu.workflowName) titleParts.push(versionMenu.workflowName);
                if (versionMenu.documentName && versionMenu.documentName !== versionMenu.workflowName) {
                  titleParts.push(versionMenu.documentName);
                }
                const documentTitle = titleParts.join(' — ') || safeName;
                // Owner-display name: prefer the email's local part since
                // we don't have a separate display-name field in DDB.
                // Falls back to "the team" if the row is genuinely empty.
                const ownerEmail = versionMenu.ownerEmail || '';
                const ownerName = ownerEmail
                  ? (ownerEmail.includes('@') ? ownerEmail.split('@')[0].replace(/[._]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : ownerEmail)
                  : '';
                try {
                  const exportBlocks = await loadExportBlocks();
                  await exportBlocks({
                    blocks: useLive ? liveAtClick : undefined,
                    blocksUrl: useLive ? undefined : blocksUrl,
                    filename: `${safeName}.${format}`,
                    format,
                    documentTitle,
                    documentMeta: {
                      ownerEmail,
                      ownerName,
                      createdAt: versionMenu.createdAt || '',
                      updatedAt: versionMenu.updatedAt || '',
                    },
                    customerName: versionMenu.customerName || '',
                    version: versionMenu.versionNumber || 1,
                  });
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error('[ProjectTree] export failed:', err);
                  alertDialog({
                    title: 'Export failed',
                    message: err?.message || 'Could not produce the file.',
                  });
                }
              };
              const noBlocks = !hasLive && !blocksUrl;
              const noBlocksTitle = 'No saved canvas yet — open the design and let the agent or you write to the canvas first.';
              return (
                <>
                  <button
                    className="tree-context-menu-item"
                    disabled={noBlocks}
                    title={noBlocks ? noBlocksTitle : undefined}
                    onClick={() => runExport('docx')}
                  >
                    <span className="tree-context-menu-item-icon">📄</span>
                    <span className="tree-context-menu-item-label">Export as .docx</span>
                  </button>
                  <button
                    className="tree-context-menu-item"
                    disabled={noBlocks}
                    title={noBlocks ? noBlocksTitle : undefined}
                    onClick={() => runExport('html')}
                  >
                    <span className="tree-context-menu-item-icon">🌐</span>
                    <span className="tree-context-menu-item-label">Export as .html</span>
                  </button>
                  <button
                    className="tree-context-menu-item"
                    disabled={noBlocks}
                    title={noBlocks ? noBlocksTitle : undefined}
                    onClick={() => runExport('json')}
                  >
                    <span className="tree-context-menu-item-icon">{'{}'}</span>
                    <span className="tree-context-menu-item-label">Export as .json</span>
                  </button>
                </>
              );
            })()}
            <div className="tree-context-menu-separator" />
            <button
              className="tree-context-menu-item"
              onClick={() => {
                setVersionMenu(null);
                const docName = versionMenu.documentName
                  || versionMenu.workflowName
                  || '';
                setRenamingDoc({
                  designId: did,
                  value: docName,
                  original: docName,
                });
              }}
            >
              <span className="tree-context-menu-item-icon"><IconPencil /></span>
              <span className="tree-context-menu-item-label">Rename</span>
            </button>
            <button
              className="tree-context-menu-item"
              onClick={async () => {
                setVersionMenu(null);
                const cid = consultantId;
                if (!cid) {
                  await alertDialog({
                    title: 'Duplicate failed',
                    message: 'Could not resolve your identity. Sign out and back in, then retry.',
                  });
                  return;
                }
                try {
                  const result = await duplicateDesign(did, cid);
                  // Invalidate the design cache so the tree refetches. Pass
                  // the designId in detail so DesignView's listener can do
                  // a targeted refresh instead of a broad reset.
                  window.dispatchEvent(new CustomEvent('ajo:design-cache-invalidated', {
                    detail: { designId: result?.designId || did },
                  }));
                  // Navigate to the new version. Duplicate now creates a new
                  // version under the SAME designId, so open it with ?v=N.
                  if (result?.designId && result?.version) {
                    navigate(`/design/${result.designId}?v=${result.version}`);
                  } else if (result?.designId) {
                    navigate(`/design/${result.designId}`);
                  }
                } catch (err) {
                  await alertDialog({
                    title: 'Duplicate failed',
                    message: err?.response?.data?.error || err?.message || 'Unknown error',
                  });
                }
              }}
            >
              <span className="tree-context-menu-item-icon"><IconCopy /></span>
              <span className="tree-context-menu-item-label">Duplicate</span>
            </button>
            <button
              className="tree-context-menu-item"
              onClick={() => {
                const url = `${window.location.origin}/design/${did}`;
                setVersionMenu(null);
                copyToClipboard(url, 'Share link copied. Recipients must sign in via Okta and be in the access group to view.');
              }}
            >
              <span className="tree-context-menu-item-icon">🔗</span>
              <span className="tree-context-menu-item-label">Copy share link</span>
            </button>
            <div className="tree-context-menu-separator" />
            <button
              className="tree-context-menu-item tree-context-menu-danger"
              disabled={isDraft}
              title={isDraft ? 'Nothing to delete — the draft is empty until you export. To remove the workflow, use the workflow ⋯ menu.' : undefined}
              onClick={async () => {
                setVersionMenu(null);
                if (isDraft) return;
                const ok = await confirmDialog({
                  title: `Delete v${v.version}`,
                  message: isOnly
                    ? `Delete the only version (v${v.version})? The workflow keeps an empty v1 draft you can rebuild from the canvas.`
                    : `Delete v${v.version}? This removes the rendered HTML / DOCX from S3 and cannot be undone.`,
                  confirmLabel: 'Delete',
                  danger: true,
                });
                if (!ok) return;
                const keys = [keyFromUrl(v.htmlUrl), keyFromUrl(v.docxUrl)].filter(Boolean);
                try {
                  for (const k of keys) {
                    await deleteWorkflowFile(k);
                  }
                  setDesignCache((prev) => {
                    const next = { ...prev };
                    delete next[did];
                    return next;
                  });
                  await ensureFullDesign(did);
                } catch (err) {
                  await alertDialog({
                    title: 'Delete failed',
                    message: err?.response?.data?.error || err?.message || 'unknown error',
                  });
                }
              }}
            >
              <span className="tree-context-menu-item-icon"><IconTrash /></span>
              <span className="tree-context-menu-item-label">
                {isDraft ? 'Delete' : `Delete v${v.version}`}
              </span>
            </button>
          </div>
        );
      })()}

      {/* File right-click context menu */}
      {fileMenu && (
        <div
          className="tree-context-menu"
          style={{ left: fileMenu.x, top: fileMenu.y }}
          onMouseDown={(e) => e.stopPropagation()}
          role="menu"
        >
          <button
            className="tree-context-menu-item"
            onClick={async () => {
              const f = fileMenu.file;
              const did = fileMenu.designId;
              setFileMenu(null);
              if (f?.s3Key && did) {
                const newWindow = window.open('', '_blank', 'noopener');
                const url = await resolveFileUrl(did, f);
                if (url && newWindow) {
                  newWindow.location.href = url;
                } else if (newWindow) {
                  newWindow.close();
                }
              }
            }}
            disabled={!fileMenu.file?.s3Key}
          >
            <span className="tree-context-menu-item-icon">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 1v8M4 6l3 3 3-3M2 10v2a1 1 0 001 1h8a1 1 0 001-1v-2" />
              </svg>
            </span>
            <span className="tree-context-menu-item-label">Download</span>
          </button>
          {/* "Open in canvas" removed — left-clicking the row already
              opens the preview, so the menu entry was redundant. */}
          {fileMenu.renameable && (
            <button
              className="tree-context-menu-item"
              onClick={() => {
                const onRename = fileMenu.onRename;
                const file = fileMenu.file;
                setFileMenu(null);
                if (onRename) onRename(file);
              }}
            >
              <span className="tree-context-menu-item-icon"><IconPencil /></span>
              <span className="tree-context-menu-item-label">Rename</span>
            </button>
          )}
          <button
            className="tree-context-menu-item"
            onClick={async () => {
              const f = fileMenu.file;
              const did = fileMenu.designId;
              setFileMenu(null);
              if (f?.s3Key && did) {
                const url = await resolveFileUrl(did, f);
                if (url) {
                  copyToClipboard(url, 'Download link copied. Pre-signed S3 URL — expires in ~10 minutes, anyone with the URL can download.');
                }
              }
            }}
            disabled={!fileMenu.file?.s3Key}
          >
            <span className="tree-context-menu-item-icon">🔗</span>
            <span className="tree-context-menu-item-label">Copy download link</span>
          </button>
          {fileMenu.deletable && (
            <>
              <div className="tree-context-menu-separator" />
              <button
                className="tree-context-menu-item tree-context-menu-danger"
                onClick={async () => {
                  if (fileMenu.onDelete) await fileMenu.onDelete(fileMenu.file);
                  setFileMenu(null);
                }}
              >
                <span className="tree-context-menu-item-icon">
                  <IconTrash />
                </span>
                <span className="tree-context-menu-item-label">Delete</span>
              </button>
            </>
          )}
        </div>
      )}

      {/* Project ⋯ context menu */}
      {projectMenu && (
        <div
          className="tree-context-menu"
          style={{ left: projectMenu.x, top: projectMenu.y }}
          onMouseDown={(e) => e.stopPropagation()}
          role="menu"
        >
          <button
            className="tree-context-menu-item"
            onClick={() => {
              onNewWorkflow?.({ projectId: projectMenu.projectId, projectName: projectMenu.projectName, shellDesignId: projectMenu.shellDesignId });
              setProjectMenu(null);
            }}
          >
            <span className="tree-context-menu-item-icon"><IconPlus /></span>
            <span className="tree-context-menu-item-label">New workflow</span>
            <span className="tree-context-menu-item-shortcut">⇧⌘N</span>
          </button>
          <button
            className="tree-context-menu-item"
            disabled={!projectMenu.shellDesignId}
            onClick={() => {
              const { projectId: pid, projectName: pname, shellDesignId } = projectMenu;
              setProjectMenu(null);
              if (!shellDesignId) return;
              setRenamingProject({
                projectId: pid,
                shellDesignId,
                value: pname || '',
                original: pname || '',
              });
            }}
          >
            <span className="tree-context-menu-item-icon"><IconPencil /></span>
            <span className="tree-context-menu-item-label">Rename</span>
          </button>
          <button
            className="tree-context-menu-item"
            onClick={() => {
              if (projectMenu.shellDesignId) openSettings(projectMenu.shellDesignId, projectMenu.projectName);
              setProjectMenu(null);
            }}
          >
            <span className="tree-context-menu-item-icon"><IconPencil /></span>
            <span className="tree-context-menu-item-label">Update project</span>
          </button>
          <button
            className="tree-context-menu-item"
            onClick={() => {
              toggleStarred(projectMenu.projectId);
              setProjectMenu(null);
            }}
          >
            <span className="tree-context-menu-item-icon">{starredIds.includes(projectMenu.projectId) ? '☆' : '★'}</span>
            <span className="tree-context-menu-item-label">
              {starredIds.includes(projectMenu.projectId) ? 'Unstar project' : 'Star project'}
            </span>
          </button>
          <div className="tree-context-menu-separator" />
          <button
            className="tree-context-menu-item tree-context-menu-danger"
            onClick={async () => {
              const did = projectMenu.shellDesignId;
              const name = projectMenu.projectName;
              setProjectMenu(null);
              if (did) await confirmAndDelete(did, name);
            }}
          >
            <span className="tree-context-menu-item-icon"><IconTrash /></span>
            <span className="tree-context-menu-item-label">Delete project</span>
          </button>
        </div>
      )}

      {/* Workflow ⋯ context menu */}
      {workflowMenu && (
        <div
          className="tree-context-menu"
          style={{ left: workflowMenu.x, top: workflowMenu.y }}
          onMouseDown={(e) => e.stopPropagation()}
          role="menu"
        >
          <button
            className="tree-context-menu-item"
            onClick={() => {
              const { designId, name } = workflowMenu;
              setWorkflowMenu(null);
              setRenamingWorkflow({ designId, value: name || '', original: name || '' });
            }}
          >
            <span className="tree-context-menu-item-icon"><IconPencil /></span>
            <span className="tree-context-menu-item-label">Rename</span>
          </button>
          {/* "Upload files" removed — uploads happen through the chat
              composer only (drag/drop or the + attach button), so there's
              one consistent way to add inputs to a workflow. */}
          <div className="tree-context-menu-separator" />
          <button
            className="tree-context-menu-item tree-context-menu-danger"
            onClick={async () => {
              const { designId, name } = workflowMenu;
              setWorkflowMenu(null);
              await confirmAndDelete(designId, name);
            }}
          >
            <span className="tree-context-menu-item-icon"><IconTrash /></span>
            <span className="tree-context-menu-item-label">Delete workflow</span>
          </button>
        </div>
      )}

      {linkCopiedMsg && (
        <div
          role="status"
          aria-live="polite"
          className="leftrail-link-toast"
          style={{
            position: 'fixed',
            left: 24,
            bottom: 24,
            maxWidth: 360,
            padding: '10px 14px',
            background: '#1f2937',
            color: '#fff',
            border: '1px solid #374151',
            borderRadius: 8,
            fontSize: 12.5,
            lineHeight: 1.45,
            boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
            zIndex: 1600,
          }}
        >
          {linkCopiedMsg}
        </div>
      )}
    </>
  );
}
